<template>
  <DefaultCard class="z-20">
    <form @submit="submit">
      <Heading class="mb-16" center>Personal details</Heading>

      <div class="mb-12">
        <div class="sm:flex sm:items-end">
          <div class="flex items-center mb-4 sm:mb-0">
            <img
              v-if="avatarPreview"
              :src="avatarPreview"
              alt="Avatar"
              class="w-24 h-24 object-cover mr-4 rounded"
            />
            <span class="text-xs sm:hidden">
              You can upload .jpg, .png
              <br />
              Max size of 3mb
            </span>
          </div>
          <div class="flex items-center">
            <input
              type="file"
              ref="avatarInput"
              id="avatar"
              class="hidden"
              @change="uploadImage"
            />
            <button
              type="button"
              class="btn btn-secondary w-full sm:w-auto rounded-lg font-semibold sm:px-8 lg:px-12 sm:mr-4"
              @click="$refs.avatarInput.click()"
            >
              + Upload Image
            </button>
            <span class="text-xs hidden sm:block">
              You can upload .jpg, .png
              <br />
              Max size of 3mb
            </span>
          </div>
        </div>
        <span class="text-xs text-canceled block mt-2" v-if="uploadError"
          >Image size is too big</span
        >
      </div>

      <div class="sm:flex mb-4">
        <div class="sm:mr-2 mb-4 sm:mb-0  flex-1">
          <label for="firstName" class="text-sm"
            >First Name<small>*</small></label
          >
          <input
            type="text"
            class="input mt-2"
            id="firstName"
            v-model="firstName"
            @input="changeInput"
          />
        </div>
        <div class="sm:ml-2 flex-1">
          <label for="lastName" class="text-sm"
            >Last Name<small>*</small></label
          >
          <input
            type="text"
            class="input mt-2"
            id="lastName"
            v-model="lastName"
            @input="changeInput"
          />
        </div>
      </div>

      <div class="sm:flex mb-4">
        <div class="sm:mr-2 flex-1 mb-4 sm:mb-0">
          <label for="email" class="text-sm">Email<small>*</small></label>
          <input
            type="email"
            class="input mt-2"
            id="email"
            v-model="email"
            @input="changeInput"
          />
        </div>
        <div class="sm:ml-2 flex-1">
          <label for="phone" class="text-sm">Phone</label>
          <input
            type="text"
            v-mask="'############'"
            class="input mt-2"
            id="phone"
            v-model="phone"
            @input="changeInput"
          />
        </div>
      </div>

      <div class="mb-12 mt-4">
        <label for="address" class="text-sm">Company Name</label>
        <input
          class="input mt-2"
          id="address"
          v-model="company"
          @input="changeInput"
        />
      </div>

      <div class="sm:flex sm:justify-between">
        <button
          type="submit"
          class="btn btn-primary w-full sm:w-auto rounded-lg font-semibold px-8 sm:px-12"
          :disabled="!canSubmit || updating"
        >
          <span v-if="!updating">Save Changes</span>
          <Spinner v-if="updating" />
        </button>

        <button
          v-show="!isVerified"
          type="button"
          class="btn btn-secondary w-full sm:w-auto rounded-lg font-semibold px-8 sm:px-12 mx-1"
          :loading="loading"
          @click="verify"
        >
          <span>Resend Email Verification</span>
        </button>

        <!-- button -->
        <button
          type="button"
          class="btn btn-secondary w-full sm:w-auto rounded-lg font-semibold px-8 sm:px-12"
          @click.prevent="showChangePasswordModal = true"
        >
          <span>Change Password</span>
        </button>

        <!-- modal -->
		<Modal v-if="showChangePasswordModal" :withoutFooter="true">
			<div slot="body">
        		<ChangePassword class="mb-8" @close="showChangePasswordModal=false" />
			</div>
		</Modal>
      </div>
    </form>
  </DefaultCard>
</template>

<script>
import ChangePassword from "@/components/common/account/ChangePassword";
import Modal from "@/components/common/Modal";
import DefaultCard from "../layout/DefaultCard";
import Heading from "../Heading";
import {
  required,
  minLength,
  email,
  maxLength
} from "vuelidate/lib/validators";
import Spinner from "../Spinner";
import Popup from "../Popup";
import PopupPlaceholder from "../PopupPlaceholder";
import { mapActions } from "vuex";
import Api from "../../../api";

export default {
  name: "AccountSettings",
  props: ["details", "updating"],
  components: {
    PopupPlaceholder,
    Spinner,
    Heading,
    DefaultCard,
    ChangePassword,
	Modal
  },
  data() {
    return {
      avatarPreview: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      image: null,
      touched: false,
      uploadError: false,
      loading: false,
      isVerified: true,
	  showChangePasswordModal: false,
    };
  },
  computed: {
    canSubmit() {
      return !this.$v.$invalid;
    }
  },
  created() {
    this.firstName = this.details.firstName;
    this.lastName = this.details.lastName;
    this.email = this.details.email;
    this.phone = this.details.phone;
    this.company = this.details.company;
    this.avatarPreview = this.details.avatar;
  },
  beforeMount() {
    Api.isVerified().then(response => {
      this.isVerified = response.data.isVerified;
    });
  },
  methods: {
    ...mapActions(["setSuccessMessage", "setErrorMessage"]),

    submit(event) {
      event.preventDefault();

      if (this.uploadError) {
        this.uploadError = false;
      }

      this.$emit("submit", {
        firstName: this.firstName,
        lastName: this.lastName,
        company: this.company,
        email: this.email,
        phone: this.phone,
        avatar: this.image
      });

      this.touched = false;
    },

    changeInput() {
      if (!this.touched) {
        this.touched = true;
      }
    },

    uploadImage(e) {
      if (this.uploadError) {
        this.uploadError = false;
      }

      const file = e.target.files[0];
      const fileSize = file.size / 1024 / 1024; // in MB
      if (fileSize > 3) {
        this.uploadError = true;
        this.$refs.avatarInput.value = null;
      } else {
        this.image = file;
        this.avatarPreview = URL.createObjectURL(file);
        this.touched = true;
      }
    },

    async verify() {
      try {
        this.loading = true;

        if (!this.isVerified) {
          await Api.resendVerifyEmail()
            .then(response => {
              if (response.status === 202) {
                this.setSuccessMessage(
                  "A verification link has been sent to your email."
                );
              }
            })
            .catch(() => {
              this.setErrorMessage(
                "The number of attempts to verify your email has been reached. Please wait 20 minutes and then try again."
              );
            });
        }

        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    }
  },
  validations: {
    firstName: {
      required
    },
    lastName: {
      required
    },
    email: {
      email,
      required
    },
    phone: {},
    company: {}
  }
};
</script>
